import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import * as Sentry from '@sentry/browser';
import { healPngIfIncomplete, resolveHealedCard, cardPlaceholderDataUri } from '../../../helpers/cardImage.js';

// Card images verified good this session. Hands re-render on every trick, so
// without this each remount would re-fetch; poisoning is a cross-session disk-
// cache fault, so a src proven good stays good for the life of the page.
const verifiedCardSrcs = new Set();

// Fires only for a healed or failed card (never the healthy majority), so Sentry
// stays quiet until a truncated copy is actually found. stage 'edge'/'failed'
// means the canonical CDN object is poisoned and needs a purge. Wrapped so a
// telemetry error can never break card rendering.
const reportCardImage = (info) => {
  try {
    Sentry.captureMessage(
      `card image ${info.status}${info.stage ? ` (${info.stage})` : ''}`,
      {
        level: info.status === 'failed' ? 'error' : 'warning',
        extra: info
      }
    );
  } catch (err) { /* never let reporting break the card */ }
};

const useStyles = makeStyles(() => ({
  enabled: {
    cursor: 'pointer'
  },
  disabled: {
    filter: 'brightness(80%)', // <--- instead of card overlay (overlay removed CardSuitGroup)
    cursor: 'not-allowed',
    '&:active': {
      animation: '$shake 0.82s cubic-bezier(.36,.07,.19,.97) both',
      transform: 'translate3d(0, 0, 0)',
      backfaceVisibility: 'hidden'
    }
  },
  '@keyframes shake': {
    '10%, 90%': {
      transform: 'translate3d(-1px, 0, 0)'
    },
    '20%, 80%': {
      transform: 'translate3d(2px, 0, 0)'
    },
    '30%, 50%, 70%': {
      transform: 'translate3d(-4px, 0, 0)'
    },
    '40%, 60%': {
      transform: 'translate3d(4px, 0, 0)'
    }
  }
}));

const Card = (props) => {
  const {
    value,
    flipped,
    enabled,
    onClick,
    cardRefCallback,
    variant = 'normal'
  } = props;

  const classes = useStyles();

  const canonicalSrc = flipped
    ? `/img/cards/${variant}/BACK.png`
    : `/img/cards/${variant}/${value}.png`;

  const [displaySrc, setDisplaySrc] = useState(canonicalSrc);
  const [failed, setFailed] = useState(false);

  // A truncated card image does NOT fire onError — the browser paints the partial
  // and reports success — so the only way to know it is broken is to read the
  // bytes. Verify this card's image and, if a poisoned cache copy is found, heal
  // it and swap in a src that re-fetches the good bytes live (no refresh). This is
  // what rescues a deep-linked customer whose card mounts before any warm-up.
  useEffect(() => {
    setDisplaySrc(canonicalSrc);
    setFailed(false);
    if (verifiedCardSrcs.has(canonicalSrc)) {
      return undefined;
    }
    let active = true;
    healPngIfIncomplete(canonicalSrc, { report: reportCardImage }).then((result) => {
      if (!active) {
        return;
      }
      const { src, failed: didFail, verified } = resolveHealedCard(result, canonicalSrc, String(Date.now()));
      if (verified) {
        verifiedCardSrcs.add(canonicalSrc);
      }
      if (didFail) {
        setFailed(true);
      } else {
        setDisplaySrc(src);
      }
    });
    return () => { active = false; };
  }, [canonicalSrc]);

  const handleClick = () => {
    if (enabled && onClick) {
      onClick(value);
    }
  };

  // Last-resort net when an image can't be recovered at all: draw the card as an
  // inline-SVG so the fallback is the SAME <img> element in the SAME box as a real
  // card (the CardSuitGroup wrapper styles every <img> identically) — keeping it
  // clickable and never disturbing the hand layout. Face-down draws a plain back.
  const src = failed ? cardPlaceholderDataUri(value, flipped) : displaySrc;

  return (
    <img
      data-testid='card-play'
      className={clsx(enabled && !flipped ? classes.enabled : classes.disabled, classes.shake)}
      style={{ width: '100%' }}
      src={src}
      alt={value}
      onClick={handleClick}
      onError={failed ? undefined : () => setFailed(true)}
      ref={cardRefCallback}
    />
  );
};

Card.propTypes = {
  value: PropTypes.string.isRequired,
  flipped: PropTypes.bool,
  enabled: PropTypes.bool,
  onClick: PropTypes.func,
  cardRefCallback: PropTypes.func
};

export default Card;
